/**
 * Color constants mapping predefined Tailwind colors to their hex values
 * These should be used instead of hardcoded hex colors throughout the codebase
 */

// Neutral colors - Light mode
export const NEUTRAL_COLORS = {
  50: '#FFFFFF',   // white
  100: '#F7F7F7',
  200: '#EFEFEF',
  300: '#E6E6E6',
  400: '#D4D4D4',
  500: '#A3A3A3',
  600: '#737373',
  700: '#525252',
  800: '#262626',
  900: '#121212',
  1000: '#000000', // black
} as const

// Neutral colors - Dark mode
export const NEUTRAL_COLORS_DARK = {
  50: '#FFFFFF',
  100: '#EDEFF2',
  200: '#D5DAE0',
  300: '#B4BBC4',
  400: '#8E98A3',
  500: '#6B7785',
  600: '#4B5561',
  700: '#343C46',
  800: '#232A32',
  900: '#141A20',
  1000: '#0B0F14',
} as const

// Gray colors (from tailwind.config.js)
export const GRAY_COLORS = {
  1: '#FFFFFF',
  2: '#E6E6E9',
  3: '#CCCCD1',
  4: '#B3B3B9',
  5: '#9999A1',
  6: '#7F7F89',
  7: '#666671',
  8: '#4C4C59',
  9: '#33333F',
  10: '#000000',
} as const

// Primary colors - Light mode
export const PRIMARY_COLORS = {
  50: '#FDF3ED',
  100: '#FCE8DD',
  200: '#F9D1BC',
  300: '#F4B28E',
  400: '#F09362',
  500: '#E97B40',
  600: '#BB5118',
  700: '#984214',
  800: '#75320F',
  900: '#52230A',
  1000: '#2B1207',
} as const

// Primary colors - Dark mode
export const PRIMARY_COLORS_DARK = {
  50: '#FFE3D0',
  100: '#FFCEAD',
  200: '#FFB78A',
  300: '#FF9F67',
  400: '#FF8A3D',
  500: '#EA651E',
  600: '#BB5118',
  700: '#8F3F13',
  800: '#6B2F0E',
  900: '#4A220A',
  1000: '#2F1507',
} as const

// Dawn Orange colors (from tailwind.config.js)
export const DAWN_ORANGE = {
  1: '#FFEDE3',
  2: '#FFD3B8',
  3: '#FFB88C',
  4: '#FF9E63',
  5: '#E97B40',
  6: '#D06936',
  7: '#B3572E',
  8: '#944625',
  9: '#75361D',
  10: '#582714',
} as const

// Info/Blue colors
export const INFO_COLORS = {
  50: '#EAF3FE',
  100: '#D1E6FD',
  200: '#A7CEF9',
  300: '#7DB6F4',
  400: '#529EF0',
  500: '#1E7AE8',
  600: '#175FBA',
  700: '#11478E',
  800: '#0B305F',
  900: '#061C38',
  1000: '#030F1C',
} as const

/**
 * Get theme-aware text color
 * Light mode: neutral-900, Dark mode: gray-1 (white)
 */
export const getTextColor = (isDarkMode: boolean): string => {
  return isDarkMode ? GRAY_COLORS[1] : NEUTRAL_COLORS[900]
}

/**
 * Get theme-aware background color for tooltips
 * Light mode: white, Dark mode: black
 */
export const getTooltipBg = (isDarkMode: boolean): string => {
  return isDarkMode ? NEUTRAL_COLORS[1000] : NEUTRAL_COLORS[50]
}

/**
 * Get theme-aware border color
 * Light mode: neutral-300, Dark mode: gray-7
 */
export const getBorderColor = (isDarkMode: boolean): string => {
  return isDarkMode ? GRAY_COLORS[7] : NEUTRAL_COLORS[300]
}

/**
 * Get theme-aware axis/grid line color
 * Uses a neutral gray that works in both modes
 */
export const getAxisLineColor = (): string => {
  return '#9ca3af' // This is a standard gray that works well for axes
}

/**
 * Chart series colors - these are data visualization colors
 * Earnings: Orange (#f97316 is close to primary-500)
 * Referrals: Blue (info-500)
 */
export const CHART_COLORS = {
  EARNINGS: PRIMARY_COLORS[500], // #E97B40
  REFERRALS: INFO_COLORS[500],    // #1E7AE8
} as const

